import {
  ChooseItem,
  PAYMENT_PLAN_TYPE,
  Promotion,
  SelectedPromotion,
} from 'core/services/bff/@types';

import type { Cart, Image } from './cart';
import type { AsyncFlow, AsyncFlowWithData } from './utilities';

const CHECKOUT_PAYMENT_VIEW = {
  PAYMENT_BANK_SLIP: 'payment.bankSlip',
  PAYMENT_CREDIT_CARD: 'payment.creditCard',
  PAYMENT_CREDIT_CARD_FORM: 'payment.creditCardForm',
  PAYMENT_ERRORS: 'payment.errors',
  PAYMENT_SELECTOR: 'payment.selector',
  PAYMENT_STORE_CREDITS: 'payment.storeCredits',
};

const CHECKOUT_SUMMARY_VIEW = {
  SUMMARY: 'summary',
};

export const CHECKOUT_VIEW = {
  ...CHECKOUT_PAYMENT_VIEW,
  ...CHECKOUT_SUMMARY_VIEW,
};

export const CHECKOUT_DEFAULT_VIEWS = {
  cart: '',
  payment: CHECKOUT_PAYMENT_VIEW.PAYMENT_SELECTOR,
  summary: CHECKOUT_SUMMARY_VIEW.SUMMARY,
};

type CreditCardDetail = {
  flag: string;
  info: Cart['data']['paymentDetail'][];
};

type CreditCard = {
  hasPaymentInfo: boolean;
  name: string;
  description: string;
  paymentInfo: CreditCardDetail[];
  textQuestion?: string;
  paymentPlanType: ValueOf<typeof PAYMENT_PLAN_TYPE>;
};

type CheckoutDefaultStep = keyof CheckoutViews;

type CheckoutStep<T = string> = {
  canSubmit: boolean;
  completed: boolean;
  view: T;
};

type CheckoutViews = {
  cart: CheckoutStep;
  summary: CheckoutStep<CheckoutSummaryView>;
  payment: CheckoutStep<CheckoutPaymentView> & { isCreditCardSelected: boolean };
};

type PaymentDetail = Cart['data']['paymentDetail'];

type PaymentMethods<B = Payments, C = CreditCard> = {
  bankSlip: B;
  creditCard: C;
  linkPayment?: B;
  centralPayment?: B;
  pixPayment?: B;
};

type Discount = {
  code: number;
  description: string;
  name: string;
};

type GiftItem = {
  image: Image;
  name: string;
  priceTotal: string;
  productCode: string;
  quantity: number;
};

type Reward = {
  discounts: Discount[];
  gifts: GiftItem[];
  choose?: ChooseItem[];
};

export type Payments = {
  name: string;
  description: string;
  paymentInfo: PaymentDetail[];
  textQuestion?: string;
  paymentPlanType: string;
};

export type CheckoutPaymentView = ValueOf<typeof CHECKOUT_PAYMENT_VIEW>;
export type CheckoutSummaryView = ValueOf<typeof CHECKOUT_SUMMARY_VIEW>;

export type Checkout = {
  step: CheckoutDefaultStep;
  views: CheckoutViews;
  order: AsyncFlow & {
    code: string;
    errno?: string | null;
  };
  paymentDetail: AsyncFlowWithData<PaymentDetail>;
  paymentMethods: AsyncFlowWithData<PaymentMethods>;
  paymentOption: PaymentDetail & {
    paymentMethod?: string;
  };
  isPaymentRequired: boolean;
  isNotificationVisible: boolean;
  isReady: boolean;
  isRunning: boolean;
  isRetry: boolean;
  isGiftSelected: boolean;
  rewards: AsyncFlowWithData<Reward>;
  selectedPromotions: SelectedPromotion[];
  shippingModel: ShippingModelCheckout;
  isLoadingPaymentPlan?: boolean;
  paymentPlanError?: boolean;
};

type ShippingModelCheckout = {
  isWithdrawalCenter: boolean;
  distributionCenterCode: number;
  distributionCenterName: string;
  address: string;
};

export type CheckoutShippingModel = {
  step: CheckoutDefaultStep;
  views: CheckoutViews;
  order: AsyncFlow & {
    code: string;
    errno?: string | null;
  };
  paymentDetail: AsyncFlowWithData<PaymentDetail>;
  paymentMethods: AsyncFlowWithData<PaymentMethods>;
  paymentOption: PaymentDetail & {
    paymentMethod?: string;
  };
  isPaymentRequired: boolean;
  isNotificationVisible: boolean;
  isReady: boolean;
  isRunning: boolean;
  isRetry: boolean;
  isGiftSelected: boolean;
  rewards: AsyncFlowWithData<Reward>;
  selectedPromotions: SelectedPromotion[];
  shippingModel: ShippingModelCheckout;
};

export type SelectedPromotionItemQuantity = { gift: ChooseItem; sku: string; index: number };
